/**
 * Security Utilities
 * Provides functions for domain validation, password strength, rate limiting, etc.
 */

import { PasswordStrength, RateLimitAttempt } from '../types/auth.types';

/**
 * Extract domain from website URL
 * @param website - Full website URL (e.g., "https://pibernat.com.br")
 * @returns Domain string (e.g., "pibernat.com.br")
 */
export function extractDomain(website: string): string {
  try {
    const url = new URL(website);
    // Return hostname without 'www.' prefix if present
    return url.hostname.replace(/^www\./, '');
  } catch (error) {
    // If URL parsing fails, try to extract domain manually
    const match = website.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s]+)/);
    return match ? match[1] : website;
  }
}

/**
 * Extract domain from email address
 * @param email - Email address (e.g., "john.doe@pibernat.com.br")
 * @returns Domain string (e.g., "pibernat.com.br")
 */
export function extractEmailDomain(email: string): string {
  const parts = email.split('@');
  return parts.length === 2 ? parts[1].toLowerCase() : '';
}

/**
 * Validate if email domain matches broker website domain
 * Handles subdomains appropriately
 * @param email - User's email address
 * @param website - Broker's website URL
 * @returns True if domains match
 */
export function validateEmailDomain(email: string, website: string): boolean {
  const emailDomain = extractEmailDomain(email);
  const websiteDomain = extractDomain(website);
  
  if (!emailDomain || !websiteDomain) {
    return false;
  }
  
  // Exact match
  if (emailDomain === websiteDomain) {
    return true;
  }
  
  // Check if email domain is a subdomain of website domain
  // e.g., sub.pibernat.com.br matches pibernat.com.br
  if (emailDomain.endsWith('.' + websiteDomain)) {
    return true;
  }
  
  return false;
}

/**
 * Validate email format using RFC 5322 compliant regex
 * @param email - Email address to validate
 * @returns True if email format is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Evaluate password strength
 * @param password - Password to evaluate
 * @returns Password strength analysis
 */
export function evaluatePasswordStrength(password: string): PasswordStrength {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  
  // Calculate score (0-4)
  let score = 0;
  const feedback: string[] = [];
  
  if (requirements.minLength) score++;
  else feedback.push('Use at least 8 characters');
  
  if (requirements.hasUppercase) score++;
  else feedback.push('Include at least one uppercase letter');
  
  if (requirements.hasLowercase) score++;
  else feedback.push('Include at least one lowercase letter');
  
  if (requirements.hasNumber) score++;
  else feedback.push('Include at least one number');
  
  if (requirements.hasSpecialChar) score++;
  else feedback.push('Include at least one special character');
  
  // Additional checks
  if (password.length >= 12) score = Math.min(score + 0.5, 5);
  
  // Common password patterns
  const commonPatterns = ['password', '123456', 'qwerty', 'abc123', 'letmein'];
  if (commonPatterns.some(pattern => password.toLowerCase().includes(pattern))) {
    score = Math.max(score - 2, 0);
    feedback.push('Avoid common password patterns');
  }
  
  const isStrong = score >= 4 && Object.values(requirements).every(v => v);
  
  return {
    isStrong,
    score: Math.floor(score),
    feedback,
    requirements,
  };
}

/**
 * Rate limiting implementation using in-memory store
 * In production, use Redis or similar distributed cache
 */
class RateLimiter {
  private attempts: Map<string, RateLimitAttempt> = new Map();
  private readonly maxAttempts = 5;
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes
  private readonly blockDurationMs = 15 * 60 * 1000; // 15 minutes
  
  /**
   * Check if identifier is rate limited
   * @param identifier - Email or IP address
   * @returns True if allowed, false if rate limited
   */
  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);
    
    if (!attempt) {
      return true;
    }
    
    // Check if still blocked
    if (attempt.blockedUntil && now < attempt.blockedUntil) {
      return false;
    }
    
    // Check if window has expired
    if (now - attempt.firstAttempt > this.windowMs) {
      // Reset window
      this.attempts.delete(identifier);
      return true;
    }
    
    // Check if max attempts exceeded
    if (attempt.attempts >= this.maxAttempts) {
      return false;
    }
    
    return true;
  }
  
  /**
   * Record a failed attempt
   * @param identifier - Email or IP address
   */
  recordAttempt(identifier: string): void {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);
    
    if (!attempt || now - attempt.firstAttempt > this.windowMs) {
      // New window
      this.attempts.set(identifier, {
        identifier,
        attempts: 1,
        firstAttempt: now,
        lastAttempt: now,
        blockedUntil: null,
      });
    } else {
      // Increment attempts
      attempt.attempts++;
      attempt.lastAttempt = now;
      
      // Block if max attempts exceeded
      if (attempt.attempts >= this.maxAttempts) {
        attempt.blockedUntil = now + this.blockDurationMs;
      }
      
      this.attempts.set(identifier, attempt);
    }
  }
  
  /**
   * Reset attempts for identifier (e.g., on successful login)
   * @param identifier - Email or IP address
   */
  reset(identifier: string): void {
    this.attempts.delete(identifier);
  }
  
  /**
   * Get remaining attempts before rate limit
   * @param identifier - Email or IP address
   * @returns Remaining attempts count
   */
  getRemainingAttempts(identifier: string): number {
    const attempt = this.attempts.get(identifier);
    if (!attempt) {
      return this.maxAttempts;
    }
    
    const now = Date.now();
    
    // Check if window expired
    if (now - attempt.firstAttempt > this.windowMs) {
      return this.maxAttempts;
    }
    
    return Math.max(0, this.maxAttempts - attempt.attempts);
  }
  
  /**
   * Get time until unblocked
   * @param identifier - Email or IP address
   * @returns Milliseconds until unblocked, or 0 if not blocked
   */
  getTimeUntilUnblocked(identifier: string): number {
    const attempt = this.attempts.get(identifier);
    if (!attempt || !attempt.blockedUntil) {
      return 0;
    }
    
    const now = Date.now();
    return Math.max(0, attempt.blockedUntil - now);
  }
  
  /**
   * Clean up expired entries (call periodically)
   */
  cleanup(): void {
    const now = Date.now();
    const toDelete: string[] = [];
    
    this.attempts.forEach((attempt, identifier) => {
      if (now - attempt.firstAttempt > this.windowMs * 2) {
        toDelete.push(identifier);
      }
    });
    
    toDelete.forEach(identifier => {
      this.attempts.delete(identifier);
    });
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();

// Clean up expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    rateLimiter.cleanup();
  }, 5 * 60 * 1000);
}

/**
 * Sanitize string to prevent XSS attacks
 * @param input - String to sanitize
 * @returns Sanitized string
 */
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '')
    .trim();
}

/**
 * Format time remaining for rate limit message
 * @param milliseconds - Time remaining in milliseconds
 * @returns Formatted string (e.g., "5 minutes", "30 seconds")
 */
export function formatTimeRemaining(milliseconds: number): string {
  const seconds = Math.ceil(milliseconds / 1000);
  
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

/**
 * Generate a secure random token for password reset
 * @param length - Token length (default: 32)
 * @returns Random token string
 */
export function generateSecureToken(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  
  // Use crypto.getRandomValues if available
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    
    for (let i = 0; i < length; i++) {
      token += chars[randomValues[i] % chars.length];
    }
  } else {
    // Fallback to Math.random (less secure, for development only)
    for (let i = 0; i < length; i++) {
      token += chars[Math.floor(Math.random() * chars.length)];
    }
  }
  
  return token;
}

/**
 * Mask email for privacy (e.g., "j***@pibernat.com.br")
 * @param email - Email to mask
 * @returns Masked email
 */
export function maskEmail(email: string): string {
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;
  
  const maskedUsername = username.length > 2
    ? username[0] + '*'.repeat(username.length - 1)
    : username;
  
  return `${maskedUsername}@${domain}`;
}
